// The plaintext INSIDE the encryption (Discussions message format). v1
// wraps what used to be raw text so richer kinds — reactions today — can
// ride the same envelopes. The server never sees any of this; a reaction
// is indistinguishable from a text out there.

export interface TextWire {
  v: 1;
  t: 'text';
  text: string;
}

export interface ReactWire {
  v: 1;
  t: 'react';
  /** Seq of the message being reacted to (total order guarantees it
   * arrived before us). */
  target: number;
  emoji: string;
  op: 'add' | 'remove';
}

/** A shared file (Layer CH). The ciphertext lives in object storage; the
 * per-file KEY and the integrity hash ride here, inside the E2EE message —
 * the server never sees either (Signal/Matrix attachment pattern). */
export interface FileWire {
  v: 1;
  t: 'file';
  objectKey: string;
  name: string;
  size: number;
  mime: string;
  /** AES-GCM IV, base64. */
  nonce: string;
  /** AES-GCM 256-bit per-file key, base64. */
  key: string;
  /** SHA-256 of the ciphertext, base64 — verify the download before decrypt. */
  hash: string;
}

export type MessageWire = TextWire | ReactWire | FileWire;

export interface FileRef {
  objectKey: string;
  name: string;
  size: number;
  mime: string;
  nonce: string;
  key: string;
  hash: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export function encodeText(text: string): Uint8Array {
  return enc.encode(JSON.stringify({ v: 1, t: 'text', text } satisfies TextWire));
}

export function encodeReaction(target: number, emoji: string, op: 'add' | 'remove'): Uint8Array {
  return enc.encode(JSON.stringify({ v: 1, t: 'react', target, emoji, op } satisfies ReactWire));
}

export function encodeFile(ref: FileRef): Uint8Array {
  return enc.encode(JSON.stringify({ v: 1, t: 'file', ...ref } satisfies FileWire));
}

/** Decode a decrypted payload. Anything that is not a known v1 shape is
 * treated as plain text — that is exactly what pre-wire messages were. */
export function decodeMessage(payload: Uint8Array): MessageWire {
  const raw = dec.decode(payload);
  try {
    const parsed = JSON.parse(raw) as MessageWire;
    if (parsed && parsed.v === 1 && parsed.t === 'text' && typeof parsed.text === 'string') {
      return parsed;
    }
    if (
      parsed &&
      parsed.v === 1 &&
      parsed.t === 'react' &&
      typeof parsed.target === 'number' &&
      typeof parsed.emoji === 'string' &&
      parsed.emoji.length <= 16 &&
      (parsed.op === 'add' || parsed.op === 'remove')
    ) {
      return parsed;
    }
    if (
      parsed &&
      parsed.v === 1 &&
      parsed.t === 'file' &&
      typeof parsed.objectKey === 'string' &&
      typeof parsed.name === 'string' &&
      typeof parsed.size === 'number' &&
      typeof parsed.key === 'string' &&
      typeof parsed.nonce === 'string' &&
      typeof parsed.hash === 'string'
    ) {
      return parsed;
    }
  } catch {
    /* not JSON — a legacy plain-text message */
  }
  return { v: 1, t: 'text', text: raw };
}

/**
 * One reaction per person per message (the Signal/WhatsApp rule): picking
 * the same emoji removes it; picking a different one REPLACES the current.
 * Returns the ops to send, in order.
 */
export function reactionOps(
  current: string | undefined,
  picked: string,
): { emoji: string; op: 'add' | 'remove' }[] {
  if (current === picked) return [{ emoji: picked, op: 'remove' }];
  if (current) {
    return [
      { emoji: current, op: 'remove' },
      { emoji: picked, op: 'add' },
    ];
  }
  return [{ emoji: picked, op: 'add' }];
}

/** The emoji this device currently holds on a message, if any. */
export function currentReaction(
  reactions: Record<string, string[]> | undefined,
  device: string,
): string | undefined {
  return Object.entries(reactions ?? {}).find(([, devices]) => devices.includes(device))?.[0];
}

/** Apply a reaction toggle to a per-message reaction map (emoji → devices). */
export function applyReaction(
  reactions: Record<string, string[]> | undefined,
  emoji: string,
  device: string,
  op: 'add' | 'remove',
): Record<string, string[]> {
  const out: Record<string, string[]> = { ...(reactions ?? {}) };
  const holders = new Set(out[emoji] ?? []);
  if (op === 'add') holders.add(device);
  else holders.delete(device);
  if (holders.size === 0) delete out[emoji];
  else out[emoji] = [...holders];
  return out;
}
